import { useRef, useState, type ReactElement } from "react";
import { ChevronDown } from "lucide-react";
import { Menu, MenuItem } from "@primitives/overlay";
import { pressHandlers } from "@primitives/press";
import { Text } from "@primitives/Text";
import type { QueueSummary } from "../../../experience-shell/queues/types";
import { senderLabel } from "./recipients";
import styles from "./FromSelector.module.css";

/**
 * FromSelector: the sender, as one word with a chevron. The menu lists "You"
 * and the shared queues the asker belongs to.
 */

export interface FromSelectorProps {
  readonly labelId: string;
  readonly options: readonly QueueSummary[];
  readonly value: string;
  readonly onChange: (queueId: string) => void;
}

export function FromSelector({
  labelId,
  options,
  value,
  onChange,
}: FromSelectorProps): ReactElement {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const current = options.find((q) => q.id === value) ?? options[0];

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-labelledby={labelId}
        className={styles.trigger}
        onClick={() => {
          setOpen((v) => !v);
        }}
        {...pressHandlers<HTMLButtonElement>({}, false)}
      >
        <Text as="span" role="body" style={{ color: "inherit" }}>
          {current !== undefined ? senderLabel(current) : "You"}
        </Text>
        <span aria-hidden="true" className={styles.chevron}>
          <ChevronDown size={14} strokeWidth={2} />
        </span>
      </button>
      <Menu
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        anchorRef={anchorRef}
        labelledBy={labelId}
        placement="bottom-start"
        sheetOnCompact={false}
        testId="from-menu"
      >
        {options.map((q) => (
          <MenuItem
            key={q.id}
            role="menuitemradio"
            checked={q.id === value}
            label={senderLabel(q)}
            {...(q.isPersonal ? {} : { description: "Shared queue" })}
            onSelect={() => {
              onChange(q.id);
              setOpen(false);
            }}
          />
        ))}
      </Menu>
    </>
  );
}
